
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ReferralTerms {
  id: string;
  version: string;
  content: string;
  effective_date: string;
  created_at: string;
  is_active: boolean;
}

export interface ReferralAgreement {
  id: string;
  user_id: string;
  terms_version: string;
  agreed_at: string;
  ip_address?: string;
  user_agent?: string;
}

export interface PayoutPreferences {
  id: string;
  user_id: string;
  payout_method: 'usdc' | 'paypal';
  usdc_address?: string;
  paypal_email?: string;
  minimum_payout: number;
  created_at: string;
  updated_at: string;
}

export const useReferralTerms = () => {
  const queryClient = useQueryClient();

  // Get active terms
  const { data: activeTerms, isLoading: isLoadingTerms } = useQuery({
    queryKey: ['referral-terms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_terms')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;
      return data as ReferralTerms;
    },
  });

  // Check if user has agreed to current terms
  const { data: userAgreement, isLoading: isLoadingAgreement } = useQuery({
    queryKey: ['referral-agreement'],
    queryFn: async () => {
      if (!activeTerms) return null;

      const { data, error } = await supabase
        .from('referral_agreements')
        .select('*')
        .eq('terms_version', activeTerms.version)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as ReferralAgreement | null;
    },
    enabled: !!activeTerms,
  });

  // Get user's payout preferences
  const { data: payoutPreferences, isLoading: isLoadingPrefs } = useQuery({
    queryKey: ['payout-preferences'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_payout_preferences')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as PayoutPreferences | null;
    },
  });

  // Agree to terms
  const agreeToTerms = useMutation({
    mutationFn: async (termsVersion: string) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('referral_agreements')
        .insert({
          user_id: user.user.id,
          terms_version: termsVersion,
          user_agent: navigator.userAgent,
        })
        .select()
        .single();

      if (error) throw error;
      return data as ReferralAgreement;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-agreement'] });
      toast.success('Terms and conditions accepted successfully!');
    },
    onError: (error: any) => {
      toast.error('Failed to accept terms: ' + error.message);
    },
  });

  // Save payout preferences - using upsert to handle duplicates
  const savePayoutPreferences = useMutation({
    mutationFn: async (preferences: Omit<PayoutPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('referral_payout_preferences')
        .upsert({
          user_id: user.user.id,
          ...preferences,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) throw error;
      return data as PayoutPreferences;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payout-preferences'] });
      toast.success('Payout preferences saved successfully!');
    },
    onError: (error: any) => {
      toast.error('Failed to save preferences: ' + error.message);
    },
  });

  return {
    activeTerms,
    isLoadingTerms,
    userAgreement,
    isLoadingAgreement,
    payoutPreferences,
    isLoadingPrefs,
    agreeToTerms,
    savePayoutPreferences,
    hasAgreedToTerms: !!userAgreement,
  };
};
